import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";

export const CLIENTS_TABLE_LIMIT = 25;

export function ordinalSuffix(day: number): string {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return "st";
    case 2:
    case 22:
      return "nd";
    case 3:
    case 23:
      return "rd";
    default:
      return "th";
  }
}

/** Matches the legacy "Mar 3rd, 2025, 4:20 PM" display used across the app. */
export function formatOrdinalDateTime(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export type ClientRecord = Record<string, unknown> & {
  _id?: string;
  name?: string;
  phone?: string;
  createdAt?: string;
};

export type ClientRow = BaseRowItem & {
  raw: ClientRecord;
  name: string;
  clientId: string;
  phone: string;
  dateCreated: string;
};

export function buildClientRows(clients: ClientRecord[]): ClientRow[] {
  return clients.map((client, index) => ({
    id: str(client._id) || `client-${index}`,
    raw: client,
    name: str(client.name) || "—",
    clientId: str(client._id) || "—",
    phone: str(client.phone) || "—",
    dateCreated: formatOrdinalDateTime(client.createdAt),
  }));
}

export function createClientColumns(
  onOpen: (row: ClientRow) => void
): ColumnDef<ClientRow, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="text-[14px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "clientId",
      header: "Client ID",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="font-mono text-[12px] text-[#869AA9]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "phone",
      header: "Phone",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Date Created",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildClientsCsv(clients: ClientRecord[]): string {
  const header = ["Name", "Phone", "Client ID", "Date created"];
  const rows = clients.map((c) => [
    csvCell(str(c.name)),
    csvCell(str(c.phone)),
    csvCell(str(c._id)),
    csvCell(formatOrdinalDateTime(c.createdAt)),
  ]);
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
